import { useState, useEffect, useRef, useCallback, useMemo } from "react";

const DEFAULT_COL_WIDTH = 150;

/**
 * Compute the visible column range for a given scroll position.
 * Pure function — no side effects.
 */
function computeRange(columns, columnWidths, scrollLeft, containerWidth, overscan) {
  const fixedColumnOffset = 62; // # column width
  const adjusted = Math.max(0, scrollLeft - fixedColumnOffset);

  const widths = columns.map((c) => columnWidths[c.id] || DEFAULT_COL_WIDTH);

  // Find first visible column (right edge past scrollLeft)
  let cumWidth = 0;
  let startIdx = 0;
  for (let i = 0; i < columns.length; i++) {
    cumWidth += widths[i];
    if (cumWidth > adjusted) {
      startIdx = i;
      break;
    }
  }

  // Find last visible column (left edge past scrollLeft + viewport)
  let endIdx = columns.length - 1;
  cumWidth = 0;
  for (let i = 0; i < columns.length; i++) {
    if (cumWidth > adjusted + containerWidth) {
      endIdx = i;
      break;
    }
    cumWidth += widths[i];
  }

  // Apply overscan
  const visibleStart = Math.max(0, startIdx - overscan);
  const visibleEnd = Math.min(columns.length - 1, endIdx + overscan);

  return { visibleStart, visibleEnd, startIdx, endIdx, widths };
}

/**
 * Column virtualization hook. Only returns the columns visible in the
 * horizontal viewport (plus an overscan buffer), along with left/right
 * spacer widths so the native scrollbar keeps the correct total width.
 *
 * Key design: scrollLeft is stored in a ref, NOT state. We only trigger
 * a React re-render when the visible column range actually changes.
 * This prevents feedback loops where re-render → spacer DOM change →
 * scroll position shift → scroll event → re-render.
 *
 * Widths come from the rendered header cells: attach `measureColumn(column.id)`
 * as each <th>'s ref. A column's stored or default width is only an estimate
 * (its header's icon, label and action buttons often need more), and spacers
 * built from estimates don't match the columns they replace, so the visible
 * columns jump sideways whenever the range changes. The estimate is only used
 * for columns that haven't been rendered yet. A rendered width doesn't depend
 * on the range, so measuring settles instead of feeding back into it.
 */
export const useVisibleColumns = (
  columns,
  columnWidths,
  scrollContainerRef,
  { overscan = 5, threshold = 40 } = {}
) => {
  const active = columns.length > threshold;

  // Rendered header widths by column id. They live in state so the range and
  // spacers are recomputed from them, but the state only changes when a
  // width does. The ref mirrors it for comparing inside the observer.
  const [measuredWidths, setMeasuredWidths] = useState({});
  const measuredWidthsRef = useRef(measuredWidths);
  const headerCellsRef = useRef(new Map()); // column id → mounted <th>
  const headerCellRefsRef = useRef(new Map()); // column id → stable ref callback
  const headerObserverRef = useRef(null);

  // Measured widths win over stored ones, which win over the default.
  const knownWidths = useMemo(
    () => ({ ...columnWidths, ...measuredWidths }),
    [columnWidths, measuredWidths]
  );

  // Store the current visible range in state (triggers re-render)
  const [range, setRange] = useState({ visibleStart: 0, visibleEnd: columns.length - 1 });
  // Store scroll metrics in refs (no re-render)
  const scrollLeftRef = useRef(0);
  const containerWidthRef = useRef(0);
  // Track the scroll info for the column indicator (updated alongside range)
  const [scrollInfo, setScrollInfo] = useState({
    startCol: 1,
    endCol: columns.length,
    totalCols: columns.length,
  });

  const updateRange = useCallback(() => {
    if (!active) return;
    const { visibleStart, visibleEnd, startIdx, endIdx } = computeRange(
      columns, knownWidths, scrollLeftRef.current, containerWidthRef.current, overscan
    );

    setRange((prev) => {
      // Only re-render if the range actually changed
      if (prev.visibleStart === visibleStart && prev.visibleEnd === visibleEnd) return prev;
      return { visibleStart, visibleEnd };
    });

    setScrollInfo((prev) => {
      const newStart = startIdx + 1;
      const newEnd = Math.min(endIdx + 1, columns.length);
      if (prev.startCol === newStart && prev.endCol === newEnd && prev.totalCols === columns.length) return prev;
      return { startCol: newStart, endCol: newEnd, totalCols: columns.length };
    });
  }, [columns, knownWidths, active, overscan]);

  useEffect(() => {
    const el = scrollContainerRef?.current;
    if (!el || !active) return;

    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        scrollLeftRef.current = el.scrollLeft;
        containerWidthRef.current = el.clientWidth;
        updateRange();
        raf = null;
      });
    };

    // Initial measure
    scrollLeftRef.current = el.scrollLeft;
    containerWidthRef.current = el.clientWidth;
    updateRange();

    el.addEventListener("scroll", onScroll, { passive: true });

    let ro;
    if (typeof ResizeObserver !== "undefined") {
      ro = new ResizeObserver(() => {
        containerWidthRef.current = el.clientWidth;
        updateRange();
      });
      ro.observe(el);
    }

    return () => {
      el.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
      if (ro) ro.disconnect();
    };
  }, [scrollContainerRef, active, updateRange]);

  // Recompute when columns change (e.g. expanding image columns)
  useEffect(() => {
    if (active) updateRange();
  }, [columns.length, active, updateRange]);

  // One observer for every mounted header cell. Header cells mount before
  // effects run, so it starts by observing the ones already registered.
  useEffect(() => {
    if (!active || typeof ResizeObserver === "undefined") return;

    const observer = new ResizeObserver(() => {
      let next = null;
      headerCellsRef.current.forEach((el, id) => {
        const width = el.getBoundingClientRect().width;
        if (width > 0 && width !== measuredWidthsRef.current[id]) {
          if (!next) next = { ...measuredWidthsRef.current };
          next[id] = width;
        }
      });
      if (!next) return;
      measuredWidthsRef.current = next;
      setMeasuredWidths(next);
    });
    headerCellsRef.current.forEach((el) => observer.observe(el, { box: "border-box" }));
    headerObserverRef.current = observer;

    return () => {
      observer.disconnect();
      headerObserverRef.current = null;
    };
  }, [active]);

  // Returns the ref for a column's <th>. It's stable per column id, so React
  // only calls it when that header cell mounts or unmounts.
  const measureColumn = useCallback(
    (columnId) => {
      if (!active) return undefined;
      let ref = headerCellRefsRef.current.get(columnId);
      if (!ref) {
        ref = (el) => {
          const cells = headerCellsRef.current;
          const observer = headerObserverRef.current;
          const prev = cells.get(columnId);
          if (prev) {
            if (observer) observer.unobserve(prev);
            cells.delete(columnId);
          }
          if (el) {
            cells.set(columnId, el);
            if (observer) observer.observe(el, { box: "border-box" });
          }
        };
        headerCellRefsRef.current.set(columnId, ref);
      }
      return ref;
    },
    [active]
  );

  // Compute spacer widths from the stable range
  if (!active) {
    return {
      visibleColumns: columns,
      leftPadding: 0,
      rightPadding: 0,
      scrollInfo: { startCol: 1, endCol: columns.length, totalCols: columns.length },
      measureColumn,
    };
  }

  const widths = columns.map((c) => knownWidths[c.id] || DEFAULT_COL_WIDTH);
  const { visibleStart, visibleEnd } = range;

  // Clamp to current column count (columns may have changed)
  const safeEnd = Math.min(visibleEnd, columns.length - 1);
  const safeStart = Math.min(visibleStart, safeEnd);

  const leftPadding = widths.slice(0, safeStart).reduce((s, w) => s + w, 0);
  const rightPadding = widths.slice(safeEnd + 1).reduce((s, w) => s + w, 0);

  return {
    visibleColumns: columns.slice(safeStart, safeEnd + 1),
    leftPadding,
    rightPadding,
    scrollInfo,
    measureColumn,
  };
};
